import BrandLogo from "@components/data-display/BrandLogo";
import BackButton from "@components/inputs/BackButton";
import { OnboardingBottomBar } from "@components/utils/OnboardingBottomBar";
import { envConfig } from "@configs/env";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { ButtonIcon, FadeUpSmall, MemberSettingAuthMode, RevealOnScroll, cn } from "kz-ui-sdk";
import { PropsWithChildren, ReactNode, useCallback, useMemo } from "react";
import { MdClose } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { DEFAULT_FEATURES, DEFAULT_TRANSITIONS, OnboardLayoutProps } from "../../types/props";

interface OnboardPageLayoutProps extends OnboardLayoutProps {}

const OnboardPageLayout = ({
  children,
  className,
  contentClassName,
  title,
  subTitle,
  variant = "standard",
  navigation = {},
  features: userFeatures = {},
  transitions: userTransitions = {},
}: OnboardPageLayoutProps) => {
  const { authMode } = useMemberSettings();
  const navigate = useNavigate();

  const features = { ...DEFAULT_FEATURES, ...userFeatures };
  const transitions = { ...DEFAULT_TRANSITIONS, ...userTransitions };

  const isWelcomePage = variant === "welcome";

  const titleStyling = useMemo(() => {
    if (!authMode) return "";
    return AUTH_MODE_STYLES[authMode]?.title || "";
  }, [authMode]);

  const handleCloseNavigation = useCallback(() => {
    if (navigation.close?.onClick) {
      navigation.close.onClick();
    } else if (navigation.close?.url) {
      navigate(navigation.close.url, navigation.close.options);
    }
  }, [navigation.close, navigate]);

  const shouldShowBackButton = features.showBackButton && (navigation.back?.url || navigation.back?.onClick);
  const shouldShowCloseButton = features.showCloseButton && (navigation.close?.url || navigation.close?.onClick);

  return (
    <div className={cn("relative flex flex-col items-center", className)}>
      {/* Back navigation button */}
      {shouldShowBackButton && (
        <BackButton
          onClick={navigation.back?.onClick}
          className="absolute start-5 top-[18px]"
          to={navigation.back?.url}
          navigateOptions={navigation.back?.options}
          data-element-id="button-onboarding-back"
        />
      )}

      {/* Close navigation button */}
      {shouldShowCloseButton && (
        <ButtonIcon
          className="absolute end-5 top-[18px]"
          icon={<MdClose size={24} />}
          variant="ghost"
          onClick={handleCloseNavigation}
          data-element-id="button-onboarding-close"
        />
      )}

      <PageContentWithTransition enableTransition={transitions.enableRevealAnimation}>
        {/* Welcome page layout: Large centered logo, no header */}
        {isWelcomePage && <WelcomePageHeader />}

        {/* Standard page layout: Small logo with optional title */}
        {!isWelcomePage && (
          <StandardPageHeader
            title={title}
            subTitle={subTitle}
            titleStyling={titleStyling}
          />
        )}

        {/* Main content area */}
        <div
          className={cn("w-full px-5", contentClassName, {
            "mt-3": !!title && !isWelcomePage,
          })}
        >
          {children}
        </div>
      </PageContentWithTransition>

      {/* Bottom bar with chat and language options */}
      <OnboardingBottomBar
        showChatButton={features.showChatButton}
        showLanguageSelect={features.showLanguageSelect}
        className={cn("px-5", {
          "animate-fade-in opacity-0 [animation-delay:700ms]": transitions.enableRevealAnimation,
        })}
      />
    </div>
  );
};

const WelcomePageHeader = () => (
  <div className="relative z-[-1] mx-auto mb-6 mt-16">
    <BrandLogo
      size="auto"
      className="mx-auto"
      style={{ maxHeight: envConfig.brand.logoMaxHeight }}
    />
  </div>
);

interface StandardPageHeaderProps {
  title?: string | ReactNode;
  subTitle?: string | ReactNode;
  titleStyling: string;
}

const StandardPageHeader = ({ title, subTitle, titleStyling }: StandardPageHeaderProps) => (
  <>
    <BrandLogo className="mt-4" />
    {title && (
      <h1
        className={cn(titleStyling, "mt-[18px] text-center text-xl font-bold text-content-base", {
          "!mb-0": !!subTitle,
        })}
      >
        {title}
      </h1>
    )}
    {subTitle && <div className="text-center text-xs leading-5 text-content-soft">{subTitle}</div>}
  </>
);

interface PageContentWithTransitionProps extends PropsWithChildren {
  enableTransition: boolean;
}

const PageContentWithTransition = ({ children, enableTransition }: PageContentWithTransitionProps) => {
  if (!enableTransition) return <>{children}</>;

  return (
    <RevealOnScroll
      animation={FadeUpSmall}
      triggerOnce
      cascade
      damping={RevealOnScroll.DAMPLING.FASTEST}
      fraction={0.13}
      childClassName="w-full"
      className="w-full"
    >
      {children}
    </RevealOnScroll>
  );
};

// Styling configuration for different auth modes
const AUTH_MODE_STYLES: Record<MemberSettingAuthMode, { title: string }> = {
  [MemberSettingAuthMode.PhonePin]: {
    title: "mb-[30px] leading-7",
  },
  [MemberSettingAuthMode.UsernamePassword]: {
    title: "mb-4 leading-8",
  },
};

export default OnboardPageLayout;
